import React, { useState, useEffect } from 'react';
import { Users, FileText, Clock } from 'lucide-react';
import { quoteService } from '../../services/quoteService';
import { clientService } from '../../services/clientService';

interface ActivityItem {
  id: string;
  type: 'quote' | 'customer';
  title: string;
  description: string;
  timestamp: string;
  icon: React.ReactNode;
}

const RecentActivity = () => {
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    let activityTimeout: NodeJS.Timeout;

    const loadActivity = async () => {
      if (!isMounted) return;
      
      setIsLoading(true);
      
      try {
        // Agregar timeout general para evitar carga infinita
        activityTimeout = setTimeout(() => {
          if (isMounted) {
            console.warn('Timeout en carga de actividad reciente');
            setIsLoading(false);
            setActivities([]);
          }
        }, 6000); // Reducido a 6 segundos

        const activityItems: ActivityItem[] = [];

        // Obtener cotizaciones recientes con timeout
        try {
          const quotesTimeout = new Promise((_, reject) => {
            setTimeout(() => reject(new Error('Timeout en getQuotes')), 3000);
          });
          
          const quotes = await Promise.race([quoteService.getQuotes(), quotesTimeout]) as any[];
          
          if (quotes && quotes.length > 0) {
            const recentQuotes = quotes
              .sort((a, b) => new Date(b.created_at || '').getTime() - new Date(a.created_at || '').getTime())
              .slice(0, 3);

            recentQuotes.forEach(quote => {
              activityItems.push({
                id: `quote-${quote.id}`,
                type: 'quote',
                title: 'Nueva cotización',
                description: quote.name,
                timestamp: quote.created_at || new Date().toISOString(),
                icon: <FileText className="w-4 h-4 text-yellow-600" />
              });
            });
          }
        } catch (error) {
          console.error('Error al obtener cotizaciones:', error);
        }

        // Obtener clientes recientes con timeout
        try {
          const customersTimeout = new Promise((_, reject) => {
            setTimeout(() => reject(new Error('Timeout en getCustomers')), 3000);
          });
          
          const customers = await Promise.race([clientService.getCustomers(), customersTimeout]) as any[];
          
          if (customers && customers.length > 0) {
            const recentCustomers = customers
              .sort((a, b) => new Date(b.created_at || '').getTime() - new Date(a.created_at || '').getTime())
              .slice(0, 2);

            recentCustomers.forEach(customer => {
              activityItems.push({
                id: `customer-${customer.id}`,
                type: 'customer',
                title: 'Nuevo cliente',
                description: customer.name,
                timestamp: customer.created_at || new Date().toISOString(),
                icon: <Users className="w-4 h-4 text-green-600" />
              });
            });
          }
        } catch (error) {
          console.error('Error al obtener clientes:', error);
        }

        // Limpiar timeout si todo fue exitoso
        clearTimeout(activityTimeout);

        if (isMounted) {
          // Ordenar y limitar actividades
          const sortedActivities = activityItems
            .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
            .slice(0, 5);

          setActivities(sortedActivities);
        }
      } catch (error) {
        console.error('Error general:', error);
        clearTimeout(activityTimeout);
        if (isMounted) {
          setActivities([]);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    // Pequeño retraso para asegurar que el componente esté montado
    const timeoutId = setTimeout(() => {
      if (isMounted) {
        loadActivity();
      }
    }, 100);
    
    return () => {
      clearTimeout(timeoutId);
      clearTimeout(activityTimeout);
      isMounted = false;
    };
  }, []);

  const formatRelativeTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays > 0) {
      return `Hace ${diffDays} día${diffDays > 1 ? 's' : ''}`;
    } else if (diffHours > 0) {
      return `Hace ${diffHours} hora${diffHours > 1 ? 's' : ''}`;
    } else if (diffMins > 0) {
      return `Hace ${diffMins} minuto${diffMins > 1 ? 's' : ''}`;
    } else {
      return 'Ahora mismo';
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900">Actividad Reciente</h3>
          <Clock className="w-4 h-4 text-gray-400" />
        </div>
        <div className="space-y-3 sm:space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="flex items-center space-x-3 py-3 border-b">
                <div className="w-8 h-8 bg-gray-200 rounded-lg"></div>
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded mb-2 w-24"></div>
                  <div className="h-3 bg-gray-200 rounded w-32"></div>
                </div>
                <div className="h-3 bg-gray-200 rounded w-16"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900">Actividad Reciente</h3>
        <Clock className="w-4 h-4 text-gray-400" />
      </div>
      
      {activities.length === 0 ? (
        <div className="text-center py-8">
          <Clock className="w-8 h-8 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">No hay actividad reciente</p>
        </div>
      ) : (
        <div className="space-y-3 sm:space-y-4">
          {activities.map((activity) => (
            <div key={activity.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between py-3 border-b space-y-2 sm:space-y-0 last:border-b-0">
              <div className="flex items-start sm:items-center space-x-3">
                <div className="p-2 bg-gray-50 rounded-lg">
                  {activity.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 text-sm sm:text-base">{activity.title}</p>
                  <p className="text-sm text-gray-500 truncate">{activity.description}</p>
                </div>
              </div>
              <span className="text-sm text-gray-500 whitespace-nowrap">{formatRelativeTime(activity.timestamp)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RecentActivity;